import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Params,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { LocalStorageService } from '@core/services/local-storage.service';
import {
  MASS_FILTER_URL_MARKER,
  REQUEST_LIST_STATE_KEY,
} from '@core/constants';
import { RequestListSavedState } from '@core/types';
import {
  getMassFilterFingerprint,
  isRqQueryTooLongForUrl,
} from '@core/helpers';

/**
 * Гард восстановления состояния списка заявок.
 *
 * Если юзер переходит на страницу списка заявок без query-параметров
 * (rqQuery / limit), а в localStorage есть сохранённое состояние
 * для единого списка, гард подставляет недостающие
 * параметры в URL ДО активации компонента. Благодаря этому первый запрос
 * на бекенд сразу уходит с фильтром — без двойного запроса
 * «сначала без фильтра, потом с фильтром».
 *
 * Параметры, уже присутствующие в URL, имеют приоритет над сохранёнными
 * (диплинки работают как раньше). После редиректа все подставленные
 * параметры есть в URL, поэтому гард пропускает навигацию — зацикливание
 * невозможно.
 *
 * Исключение — массовый фильтр: набор значений в адрес не пишется (длинный
 * URL ломает перезагрузку страницы), поэтому вместо него гард подставляет
 * короткий маркер, а сам фильтр компонент списка берёт из сохранённого
 * состояния.
 */
export const RestoreRequestListStateGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const router = inject(Router);
  const saved = inject(LocalStorageService).getItem<RequestListSavedState>(
    REQUEST_LIST_STATE_KEY,
  );

  if (!saved || typeof saved !== 'object' || Array.isArray(saved)) {
    return true;
  }

  const queryParamMap = route.queryParamMap;
  // Подставляем только те параметры, которых нет в URL
  const missingParams: Params = {};
  const hasSavedFilter =
    !!saved.rqQuery &&
    typeof saved.rqQuery === 'object' &&
    Object.keys(saved.rqQuery).length > 0;

  if (
    hasSavedFilter &&
    !queryParamMap.has('rqQuery') &&
    !queryParamMap.has(MASS_FILTER_URL_MARKER)
  ) {
    const serialized = JSON.stringify(saved.rqQuery);

    if (isRqQueryTooLongForUrl(serialized)) {
      missingParams[MASS_FILTER_URL_MARKER] = getMassFilterFingerprint(
        saved.rqQuery,
      );
    } else {
      missingParams['rqQuery'] = serialized;
    }
  }
  if (!queryParamMap.has('limit') && Number(saved.limit) > 0) {
    missingParams['limit'] = saved.limit;
  }
  // Всё уже есть в URL — пропускаем (защита от бесконечного редиректа)
  if (Object.keys(missingParams).length === 0) {
    return true;
  }

  // parseUrl сохраняет путь и уже существующие query-параметры (например timestamp)
  const urlTree = router.parseUrl(state.url);

  urlTree.queryParams = { ...urlTree.queryParams, ...missingParams };

  return urlTree;
};
